import { ArrowDownTrayIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

export default function EnrichmentTool() {
  return (
    <div>
      <div>
        <h5>
          <img src="/static/images/icons/iconsPink/dna.svg" alt="R package" className="inline w-8 h-8" />
          <img
            src="/static/images/icons/iconsPink/test-tube.svg"
            alt="AIDA"
            className="inline w-6 h-6 mr-2"
          />
          Gene & Variant-Disease Enrichment Analysis
        </h5>
        <p>
          Enrichment analysis identifies diseases that are statistically over-represented in a given list of
          biological entities (genes or variants).
        </p>
        <ul>
          <li className="bullet-item">
            For a <strong>gene list</strong>, the analysis evaluates which diseases are associated with those
            genes.
          </li>
          <li className="bullet-item">
            For a <strong>variant list</strong>, the analysis evaluates which diseases are associated with
            those variants.
          </li>
        </ul>
        <p>
          In both cases, results are computed relative to a defined background (universe), representing all
          entities in the selected data source.
        </p>
      </div>

      <div className="divider"></div>
      <div className="mt-4">
        <h5 className="row">
          <img
            src="/static/images/icons/iconsPink/calculator.svg"
            alt="calculator"
            className="w-6 h-6 mr-2"
          />
          How the results are calculated
        </h5>
        <p>Each row in the results corresponds to a disease and includes:</p>
        <ul>
          <li className="bullet-item">
            <strong>Intersection:</strong>
          </li>
          <p className="ml-6">
            {' '}
            Number of input entities (genes or variants) associated with the disease (i.e. the overlap between
            your input list and the disease-associated entities)
          </p>
          <li className="bullet-item">
            <strong>Ratio (geneRatio / variantRatio):</strong>
          </li>
          <p className="ml-6">
            {' '}
            Proportion of input entities associated with the disease → (number of overlapping entities) /
            (total number of input entities)
          </p>
          <li className="bullet-item">
            <strong>bgRatio:</strong>
          </li>
          <p className="ml-6">
            {' '}
            Proportion of entities associated with the disease in the background (universe) → (number of
            entities associated with the disease) / (total number of entities in the background)
          </p>
          <li className="bullet-item">
            <strong>p-value:</strong>
          </li>
          <p className="ml-6">
            {' '}
            Statistical significance computed using a hypergeometric test, indicating whether the observed
            overlap is greater than expected by chance
          </p>
          <li className="bullet-item">
            <strong>Odds Ratio:</strong>
          </li>
          <p className="ml-6">
            {' '}
            Measure of the strength of association between your input list and the disease
            <div className="flex flex-col">
              <span>
                <ChevronRightIcon className="w-4 h-4 inline-block mr-1 text-pink" />
                Compares the <strong>observed overlap</strong> to what would be{' '}
                <strong>expected by chance</strong>{' '}
              </span>
              <span>
                <ChevronRightIcon className="w-4 h-4 inline-block mr-1 text-pink" />
                Values &gt; 1 indicate enrichment (over-representation)
              </span>
              <span>
                <ChevronRightIcon className="w-4 h-4 inline-block mr-1 text-pink" />
                Values &lt; 1 indicate depletion (under-representation)
              </span>
              <span>
                <ChevronRightIcon className="w-4 h-4 inline-block mr-1 text-pink" />
                Values ≈ 1 indicate no association
              </span>
            </div>
          </p>
          <li className="bullet-item">
            <strong>Confidence interval:</strong>
          </li>
          <p className="ml-6">
            {' '}
            Range of values that estimates the uncertainty around the odds ratio
            <div className="flex flex-col">
              <span>
                <ChevronRightIcon className="w-4 h-4 inline-block mr-1 text-pink" />
                Narrow intervals indicate more precise estimates{' '}
              </span>
              <span>
                <ChevronRightIcon className="w-4 h-4 inline-block mr-1 text-pink" />
                If the interval does not include 1, the association is typically considered statistically
                significant
              </span>
            </div>
          </p>
        </ul>
      </div>
      <div className="divider"></div>
      <div className="mt-4">
        <h5 className="row">
          <ArrowDownTrayIcon className="w-5 h-5 mr-2 text-pink" />
          Input requirements
        </h5>
        <ul>
          <li className="bullet-item">
            Provide at least <strong>one identifier</strong> (gene or variant)
          </li>
          <li className="bullet-item">
            Supported gene formats:
            <ul>
              <li>
                <ChevronRightIcon className="w-4 h-4 inline-block mr-1 text-pink" />
                NCBI Gene IDs
              </li>
              <li>
                <ChevronRightIcon className="w-4 h-4 inline-block mr-1 text-pink" />
                Ensembl IDs
              </li>
              <li>
                <ChevronRightIcon className="w-4 h-4 inline-block mr-1 text-pink" />
                HGNC symbols
              </li>
              <li>
                <ChevronRightIcon className="w-4 h-4 inline-block mr-1 text-pink" />
                UniProt IDs
              </li>
            </ul>
          </li>
          <li className="bullet-item">
            Limits:
            <ul>
              <li>
                <ChevronRightIcon className="w-4 h-4 inline-block mr-1 text-pink" />
                Up to <strong>4000 input items per query</strong>
              </li>
              <li>
                <ChevronRightIcon className="w-4 h-4 inline-block mr-1 text-pink" />
                Trial accounts: up to <strong>10 items</strong>
              </li>
            </ul>
          </li>
        </ul>
      </div>
    </div>
  );
}
